// ============================================================================
// ui/present.js — presentation mode: what a SHARED design opens on
// ============================================================================
// Phase 1 of `Weight-Designer-Bot/PRESENTATION-MODE-PLAN.md`. A rep (or a buyer)
// sends a `?d=` link; the recipient should not land in a half-explained editor,
// they should land on THEIR plate, presented — then choose to step inside.
//
// ── WHY THIS IS NOT `ui/showcase.js` ─────────────────────────────────────────
// The parade and this are the same *picture* and a different *lifecycle*. The
// parade is an INTRO: it exists to fill a blank stage, its chip is hidden once a
// logo lands (`isIntro` requires `!ui.hasLogo`), and `retire()` kills it
// permanently the moment a buyer commits one. A presented design always has a
// logo, so every one of those gates reads "never show this" — bending them would
// mean teaching the intro that it is sometimes not an intro.
//   What IS shared is the part worth sharing: the stage. Both drive the same
// three `ctx.stage` calls and the same `onActiveChange` chrome handshake in
// main.js, so the plate behaves identically under either. Only the chrome around
// it and the way out differ.
//
// ── A SET, NOT A PLATE (2026-08-16) ─────────────────────────────────────────
// Ryan: *"if you're sending a presentation with multiple models in it… the share
// needs to include all."* A buyer picks several products and designs each; the
// link now carries every one of them (`state.ui.sharedSet`, written by the boot
// path from the share record), and the arrows walk them in the order the
// switcher showed them.
//
// ⛔ A FLIP IS A REAL PRODUCT SWITCH, NOT A TEXTURE SWAP. The parade can crossfade
// because every slide is drawn on the ONE model already on the stage. A set is
// bumpers AND dumbbells — different geometry — so a flip goes through
// `ctx.applyEnvelope`, which is the app's own wholesale restore and does the
// model rebuild and the switch choreography itself. That is deliberately the live
// path: the alternative is a second renderer that agrees with the first until the
// day it doesn't (memory `wd-parade-must-mirror-the-live-render-path`).
//
// ── THE ENTRY IS A `buildRaster`, AND THAT IS LOAD-BEARING TWICE ─────────────
// It plays the dramatic reveal (the set-piece Ryan asked the link to open on),
// and — quietly more important — `ctx.stage.buildRaster` calls `syncLogoRaster`
// on the profile it is handed. A restored envelope ALWAYS arrives with
// `logo.renderUrl` null, because `serializeLogo` drops the composite on purpose
// (memory `wd-renderurl-transient-repeat-offender`); without a rebuild the plate
// draws the buyer's ORIGINAL artwork rather than the peeled/keylined mark they
// approved. Going through the stage means a shared design cannot hit that,
// because the one call that fixes it is the same call that draws it.
//
// ⛔ NEVER A SCREEN WITH NO DOOR. `enter()` refuses unless the stage can actually
// show something, and "Edit this design" is rendered unconditionally at every
// viewport — not revealed by a media query the way the parade's phone-only escape
// is. That is also what lets this mode clear the left pane and the step tabs on
// DESKTOP (`wd-presenting-share`), which the parade cannot: its only desktop exit
// lives inside that pane.
// ============================================================================

import { h } from './dom.js';
import { parseProductKey, categoryLabelOne } from '../products.js';
import { isRepMode } from '../rep-mode.js';

/* How long the outgoing model label takes to step aside before the new one arrives. Matches the
   CSS transition on `.wd-present__model` — one number in two places is a drift hazard, but a
   transition duration cannot be read back reliably before the element is in the DOM, so the two are
   stated together and the stylesheet names this constant. */
const HEADER_SWAP_MS = 160;

/* The rescue net on the settle wait — see `go()`. Comfortably outlasts a normal roll (~1s) so it
   never pre-empts the intended order, and bounds the wait if the promise never arrives. */
const SETTLE_NET_MS = 2600;

/* The site's own header logo (light on dark), off the Shopify CDN at 2x for its 34px height. */
const LOGO_URL = 'https://www.blueskyfitsupply.com/cdn/shop/files/Secondary_PNG_35b1c4e0-5455-4a49-8723-ae27bbcd2092.png?width=400';

/* How far the logo sits above the window and the contact line below it — one number, so the two
   frame the window evenly (Ryan 2026-10-28 picked the letterhead placement, "B"). The stylesheet
   reads it as `--wd-surround-gap`. */
const SURROUND_GAP_PX = 18;

/** Chevron, matching the parade's arrows (stroke, currentColor, no icon font). */
const ARROW_ICON = '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg>';

/** "Competition Urethane · Bumper Plate" for a product key, or '' when it cannot be read. */
function productLabel(key) {
  try {
    const { kind, category } = parseProductKey(key);
    return [kind || '', categoryLabelOne(category) || ''].filter(Boolean).join(' · ');
  } catch { return ''; }
}

/**
 * @param {object} ctx  the app context from main.js (needs `state`, `stage`, `announce`)
 * @param {object} opts
 * @param {(active: boolean) => number} opts.onActiveChange  the SAME handshake the parade uses —
 *        clears the stage chrome and answers with how long the stage needs before a set-piece may
 *        start on it. Shared rather than re-derived: two copies of this would disagree the day
 *        either moved (see the project CLAUDE.md, "when two places need the same answer").
 * @param {() => void} opts.onEdit  the host's "they want the real tool now" hook.
 */
export function createPresentation(ctx, opts = {}) {
  const { onActiveChange, onEdit } = opts;

  let active = false;
  /** Guards the deferred set-piece: a fast Edit must not be overtaken by a spin arriving late. */
  let enterToken = 0;
  /** @type {Array<{product: string, design: string}>} */
  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-01) — WHAT THIS SLIDE IS CALLED.
     A set has always been one entry per PRODUCT, so the product's own name was the only label it
     could need. A chain share is several VERSIONS of one product — the customer's original and the
     ones we made from it — where every slide would otherwise read "Bumper Plates" and the arrows
     would look broken. An entry may now carry its own `label`; the product name stays the default,
     so every share written before this reads exactly as it did. */
  const slideLabel = (s) => String((s && s.label) || '').trim() || productLabel((s && s.product) || '');

  let slides = [];
  /** Which slide is ON THE STAGE — not which one is selected. They differ only mid-flip. */
  let loaded = 0;
  let flipping = false;

  // ── chrome ─────────────────────────────────────────────────────────────────
  const titleEl = h('div', { class: 'wd-present__title' }, '');
  const modelEl = h('div', { class: 'wd-present__model' }, '');
  const dotsEl = h('div', { class: 'wd-present__dots', role: 'tablist', 'aria-label': 'Models in this design' });
  const header = h('div', { class: 'wd-present__header' }, titleEl, modelEl, dotsEl);

  const prevBtn = h('button', {
    type: 'button', class: 'wd-present__arrow wd-present__arrow--prev wd-flat-glass',
    'aria-label': 'Previous model', hidden: true, onClick: () => void go(loaded - 1),
  }, h('span', { 'aria-hidden': 'true', html: ARROW_ICON }));
  const nextBtn = h('button', {
    type: 'button', class: 'wd-present__arrow wd-present__arrow--next wd-flat-glass',
    'aria-label': 'Next model', hidden: true, onClick: () => void go(loaded + 1),
  }, h('span', { 'aria-hidden': 'true', html: ARROW_ICON }));

  /* ⚠ TEXT IN THE BOTTOM-LEFT CORNER, MIRRORING "POWERED BY BLUE SKY FITNESS SUPPLY" (Ryan
     2026-08-16: *"just text, no pill"*, then *"should go into the bottom left corner, same
     style/alignment as the 'POWERED BY…' in the bottom right — just underline it so people know
     it's a hyperlinked button/text"*).
       So it takes `.ds-powered-by`'s exact treatment — same corner inset, same uppercase 11px/700
       at 0.5px tracking, same ink, same 0.75 opacity — and adds the one thing that badge must never
       have: an underline, because this one is pressable and that one is not. Two credits in opposite
       corners, one of which is a control.
     ⛔ NO ICON. It was a pencil glyph while this was a pill; against a 11px uppercase credit it reads
     as debris. The underline is the affordance now. */
  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — ⛔ A CUSTOMER EDITS THEIR OWN DESIGN, and
   the button is still the one unconditional way out. Ryan: *"the 'EDIT THIS DESIGN' link should only
   be visible to the customer on the one which is their own."* Hiding it on our slides would be the
   literal reading and would trap a viewer on a screen whose only other exits are a keyboard arrow
   and Escape — the one thing this module is written never to do.
     So it RE-AIMS instead of disappearing: on a slide that is not theirs it reads "Edit my design"
   and takes them to theirs on the way out. Ryan's rule is kept where it actually matters, at the
   WRITE — only their own submission can be saved back (`/wd-share-revise`), and nothing a customer
   does to a slide of ours can leave their browser.
     ⚠️ A rep is exempt: they own every rung, and steering them off the version they opened would be
   the app arguing with the person who made it. */
  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — ⛔ ONE BUTTON, ONE DESIGN: THEIRS. Ryan,
     after two rounds of half-measures: *"The button should read 'EDIT YOUR DESIGN', it should ONLY
     be visible if a shared presentation includes at least one slide which is the original one the
     customer submitted, and it should open up their submitted design in the editor when clicked,
     every time, regardless of what slide the viewer is on when they click it."*

     Two ways a link can contain their design, and both have to be honoured:
       · a FLAGGED slide (`authored: 'customer'`), which is how a combined link carries it
       · the whole record being their submission — `ordered` is written by the quote handoff alone,
         so a share carrying one IS what they sent us, and every slide on it is theirs. Boot parks
         that verdict on `state.ui.sharedOrigin`.

     ⛔ AND IT HIDES WHEN NEITHER HOLDS, including on a link minted before slides carried an owner.
     Those cannot say which slide is whose, so the button cannot keep its promise — and hiding it is
     the safe direction to be wrong in: it withdraws an edit affordance rather than offering one over
     somebody else's work. Re-copying such a link rewrites it in place with the flags.

     ⚠️ A REP IS EXEMPT and keeps "Edit this design" on the current slide. They own every rung, and a
     rep who opened a link to fix the version on screen must not be steered onto the customer's.

     ⚠️ RYAN OVERRULED THE "Close" FALLBACK I shipped an hour ago, having been told it was the only
     exit a phone has from a full-screen overlay. Recorded rather than re-argued: on a link holding
     none of their designs, a touch viewer now leaves by closing the tab. */
  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — ⛔ AND A LINK ALREADY SENT IS RECOGNISED BY
     THE SENTENCE ON IT. Ryan: *"the link which is to their design + our design, it includes NO
     button for 'EDIT YOUR DESIGN' now."* Correct for the rule and wrong for him: that record was
     written between WD 383 and WD 391, so it carries the customer's HEADLINE and not the flag, and
     the strict reading hides the button on every combined link in every inbox.

     ⛔ MATCHING ON COPY, WHICH IS NORMALLY THE MISTAKE. Two things make it safe here and neither is
     "it works": it is scoped to records with NO owner on any slide, so it can never override a
     flagged link; and those records are FROZEN in that era — the string cannot drift out from under
     it, because nothing will ever write that wording again. The moment such a link is re-copied it
     gains real flags and this branch stops being consulted for it.

     ⚠️ A legacy slide still carries no `origin`, so "Save my changes" stays off on these. The button
     this restores is the one that OPENS their design; writing back needs the record their edit
     belongs to, and a link that never recorded one cannot supply it. */
  const LEGACY_OWN_LABEL = 'Your custom weight design';
  const ownIndex = () => {
    const flagged = slides.findIndex((s) => s && s.authored === 'customer');
    if (flagged >= 0) return flagged;
    if (slides.some((s) => s && s.authored)) return -1;
    return slides.findIndex((s) => s && String(s.label || '').trim() === LEGACY_OWN_LABEL);
  };
  /* The whole record is their submission, so wherever they are standing they are on their own. */
  const wholeLinkIsTheirs = () => !!(ctx.state?.ui?.sharedOrigin);
  const showEdit = () => isRepMode() || wholeLinkIsTheirs() || ownIndex() >= 0;

  const editBtn = h(
    'button',
    {
      type: 'button',
      class: 'wd-present__action',
      onClick: async () => {
        /* ⛔ IT GOES TO THEIR SLIDE FIRST. The editor opens on whatever the stage is holding, so a
           button that only re-labelled would still hand them the version they happened to be
           looking at — the exact fault Ryan reported twice. */
        if (!isRepMode()) {
          const i = ownIndex();
          if (i >= 0 && i !== loaded) await go(i);
        }
        leave();
      },
    },
    'Edit your design',
  );

  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-23) — the corner door, always shown: the GLD
     presentation's "Try the free Gym Layout Designer" made for plates. Bottom-LEFT, beside "Edit
     your design" in the same `.wd-present__actions` row, wearing the same face (Ryan: the
     powered-by credit's alignment, no arrow). Unlike "Edit your design" it is NOT gated on the
     visitor owning a slide — a packet link is all rep slides and had no visible way into the
     editor at all. It leaves the presentation in place (`leave()`): the same design stays on the
     stage, the viewport lock lets go, and the page's header and footer come back — which is the
     editor on the main site. Saving is still governed by `leave()`'s own rule: a rep's slide
     clears `sharedOrigin`, so a visitor cannot write over the rep's record. */
  const tryEl = h(
    'a',
    { class: 'wd-present__action wd-present__try', href: '#', onClick: (e) => { e.preventDefault(); leave(); } },
    'Try the free Weight Designer',
  );

  const actions = h('div', { class: 'wd-present__actions' }, editBtn, tryEl);

  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-10-28) — THE SURROUND: our logo just above the
     window's left edge, and the rep's "name | phone | email" just below it, like the contact line
     on the ECORE sheets. Ryan: the page was *"a little boring"*, the window itself *"great, nothing
     to change"*. Picked from mock-ups on the live page: the letterhead placement, no button (*"these
     renderings normally get sent in an email where the rep is asking just that"*), and no
     "Designed for you by" label (it pushed the name off the phone number's line).

     ⚠️ On the ROOT, not in the overlay. The overlay sits inside the canvas, which clips to its
     rounded window, so anything above or below the window has to live outside it. On desktop the
     two are placed from the overlay's measured box (`placeSurround`), because the window's left edge
     is not the frame's (the hidden pane's column sits between them). On a phone the logo takes the
     empty step-tab row beside the menu button and the contact line is a strip under the frame.

     ⛔ A link with no rep shows no contact line — a buyer sharing their own design has nobody to
     name, and a line naming the wrong person is worse than none. */
  const logoEl = h('img', { class: 'wd-present-logo', src: LOGO_URL, alt: 'Blue Sky Fitness Supply', decoding: 'async', hidden: true });
  const contactEl = h('div', { class: 'wd-present-contact', hidden: true });

  function paintContact() {
    const c = ctx.state?.ui?.sharedContact;
    contactEl.replaceChildren();
    if (!c || !c.name) { contactEl.hidden = true; return; }
    const digits = String(c.phone || '').replace(/[^\d+]/g, '');
    contactEl.append(
      h('span', { class: 'wd-present-contact__name' }, c.name),
      c.phone ? h('a', { class: 'wd-present-contact__phone', href: digits ? `tel:${digits}` : null }, c.phone) : null,
      c.email ? h('a', { class: 'wd-present-contact__email', href: `mailto:${c.email}` }, c.email) : null,
    );
    contactEl.hidden = false;
  }

  /** Pin the logo and the contact line to the window's measured edges, and drop either that has no room. */
  function placeSurround() {
    if (!active) return;
    const host = contactEl.parentElement;
    if (!host) return;
    const r = overlay.getBoundingClientRect();
    const hr = host.getBoundingClientRect();
    if (!r.width || !r.height) return;
    const top = r.top - hr.top;
    const bottom = hr.bottom - r.bottom;
    host.style.setProperty('--wd-win-left', `${Math.round(r.left - hr.left)}px`);
    host.style.setProperty('--wd-win-top', `${Math.round(top)}px`);
    host.style.setProperty('--wd-win-bottom', `${Math.round(bottom)}px`);
    host.style.setProperty('--wd-win-width', `${Math.round(r.width)}px`);
    host.style.setProperty('--wd-surround-gap', `${SURROUND_GAP_PX}px`);
    // Desktop only: a short window leaves no band above or below — then the piece simply is not shown.
    // Each piece needs its own height of room: the 34px logo above, the ~20px line of type below.
    logoEl.classList.toggle('is-no-room', top < 2 * SURROUND_GAP_PX + 34);
    contactEl.classList.toggle('is-no-room', bottom < 2 * SURROUND_GAP_PX + 20);
    /* A long name or email on a phone wraps the email onto its own line (the row is `flex-wrap`), and
       then it must lose its leading divider. Read off where the email LANDED, not off an overflow:
       a wrapping row never overflows, so a width test never fires. */
    contactEl.classList.remove('is-wrapped');
    const nameEl = contactEl.querySelector('.wd-present-contact__name');
    const mailEl = contactEl.querySelector('.wd-present-contact__email');
    contactEl.classList.toggle('is-wrapped', !!(nameEl && mailEl && mailEl.offsetTop > nameEl.offsetTop + 2));
  }
  let placeTimers = [];
  const onResize = () => placeSurround();
  function showSurround() {
    paintContact();
    logoEl.hidden = false;
    placeSurround();
    // The window settles into place over the entrance; re-measure as it does.
    placeTimers = [0, 350, 900].map((ms) => window.setTimeout(placeSurround, ms));
    window.addEventListener('resize', onResize);
  }
  function hideSurround() {
    placeTimers.forEach((t) => window.clearTimeout(t));
    placeTimers = [];
    window.removeEventListener('resize', onResize);
    logoEl.hidden = true;
    contactEl.hidden = true;
  }

  const overlay = h(
    'div',
    {
      class: 'wd-present',
      hidden: true,
      role: 'dialog',
      'aria-label': 'Shared design',
      tabindex: '-1',
      onKeydown: (e) => {
        if (!active) return;
        if (e.key === 'Escape') { e.preventDefault(); leave(); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); void go(loaded + 1); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); void go(loaded - 1); }
      },
    },
    header,
    prevBtn,
    nextBtn,
    actions,
  );

  /**
   * The line above the plate. The org is the point — it is THEIR logo on OUR equipment, and the
   * name is the half of that the picture cannot say.
   * ⚠ Read at ENTER, not at construction: the profile is restored from the share envelope during
   * boot, and this module is built with the rest of the shell.
   */
  function paintHeader() {
    const org = String(ctx.state?.designProfile?.orgName || '').trim();
    /* ⛔ THE FULL NAME, ON BOTH DESKTOP AND MOBILE (Ryan 2026-08-26: *"it should be our full name,
       Blue Sky Fitness Supply on both desktop and mobile"*). This was the ONLY place in the app
       still saying "Blue Sky Fit Supply" — every other surface (the powered-by badge, the download
       filenames, the share text, the help footer, product-info) already spells it out, so this line
       was contradicting the credit sitting two inches below it on the same screen. It is also the
       most-read line the company has: it is the title of a design sent to a customer. */
    /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — WHOSE DESIGN THIS IS, said out loud.
       Ryan gave four sentences (see `slideLabelFor` in ui/share.js, which writes them); this is
       where they land. A share minted from the designer now carries one per slide.

       ⚠️ ONLY THE ENTRY'S OWN LABEL, never the product-name fallback. A buyer's multi-product share
       labels its slides "Dumbbells" and "Bumper Plates", and hoisting one of those into the title
       would replace a sentence about the company with the name of a plate.

       ⛔ WHERE IT GOES DEPENDS ON WHAT ELSE IS THERE, because two lines saying the same thing is
       worse than one:
         · an org name always wins the title — it is THEIR name on OUR equipment, and the sentence
           moves to the line below it
         · with no org and ONE slide, the sentence IS the title
         · with no org and several, the title stays generic and the sentence rides the model line,
           which is the line that changes as they arrow through */
    const many = slides.length > 1;
    const HOUSE = 'A custom design from Blue Sky Fitness Supply';
    const entryLabel = String((slides[loaded] && slides[loaded].label) || '').trim();

    /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — ⛔ THE CUSTOMER'S OWN SUBMISSION IS
       RECOGNISED, NOT LABELLED. Ryan asked whether deleting his copies and re-adopting from the
       original email would un-stale that link's header. It would not: a share is immutable, its
       labels are written when it is MINTED, and the link in the buyer's inbox was minted by the
       buyer's own submission long before there were any labels to write. Nothing done on our side
       afterwards can reach back into it.
         What CAN be read at display time is what the record already carries: `ordered` is the
       quote the buyer submitted with it, present on their submission and on nothing else, and boot
       parks it at `state.ui.sharedOrdered`. So the sentence is DERIVED for those links rather than
       stored — which fixes every one already sitting in a customer's inbox, not just the ones minted
       from today on. */
    const fromCustomer = Array.isArray(ctx.state?.ui?.sharedOrdered) && ctx.state.ui.sharedOrdered.length > 0;
    const headline = entryLabel || (fromCustomer ? 'Your custom weight design' : '');

    /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — ⛔ THE SENTENCE IS THE TOP LINE, ALWAYS.
       The first pass put it on the model line whenever there was more than one slide, on the
       reasoning that the title should not repeat the line under it. Ryan opened a two-version link
       and reported the top line still reading the house default — which is exactly what that rule
       produces, and it is not what he asked for: *"the top header ... should read 'Your custom
       weight design'"*. The header IS the title. It changes per slide, which is the point.

       ⚠️ THE ORG LINE MOVES DOWN RATHER THAN AWAY. "Lincoln High School × Blue Sky Fitness Supply"
       is their name on our equipment and is worth keeping; it is simply not the headline any more. */
    titleEl.textContent = headline || (org ? `${org} × Blue Sky Fitness Supply` : HOUSE);

    /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — ⛔ THE SUB-LINE NAMES WHAT IS ON THE
       PLATE, always. The previous pass suppressed it whenever a share held one product, on the
       reasoning that repeating "Bumper Plates" under every slide of a chain said nothing. It said
       what they are looking at, which is the one thing the picture cannot: Ryan: *"there used to be
       sub-text on these share links which would say the actual weight they're looking at, but
       that's gone away. need to bring that back."*
       ⚠️ THE PRODUCT, NOT THE SLIDE LABEL. The label is now the title; putting it here too would
       print the same sentence twice. The org line takes this slot only when there is no product to
       name. */
    /* ⛔ AND THE STAGE ANSWERS WHEN THE SET CANNOT. `slides` is built from the share's `set`
       (:381), and a share written without one — which is every single-product buyer submission, and
       every link older than sets — leaves it EMPTY. So on a link carrying only the customer's own
       design there was no slide to read a product off and the sub-line vanished, which is exactly
       the link most likely to be the first thing a committee opens. Ryan: *"when we share the link
       only to their design, the header drops the sub-text that explains what the item is."*
       ⚠️ THE LIVE PRODUCT IS THE HONEST FALLBACK, not a guess: the shared envelope has already been
       applied by the time this paints, so `state.ui.product` IS the plate on the stage. */
    const _product = productLabel(
      (slides[loaded] && slides[loaded].product) || ctx.state?.ui?.product || '',
    );
    modelEl.textContent = _product || (org ? `${org} × Blue Sky Fitness Supply` : '');
    modelEl.hidden = !modelEl.textContent;
    /* ⚠️ `display`, NOT `hidden`. `.wd-present__action` is laid out by its own rule, and an explicit
       display beats the hidden attribute every time — a button "hidden" that way stays on screen. */
    const _show = showEdit();
    editBtn.style.display = _show ? '' : 'none';
    editBtn.textContent = isRepMode() ? 'Edit this design' : 'Edit your design';
    dotsEl.hidden = !many;
    prevBtn.hidden = !many;
    nextBtn.hidden = !many;
    if (!many) return;

    Array.from(dotsEl.children).forEach((d, i) => {
      const on = i === loaded;
      d.classList.toggle('is-active', on);
      d.setAttribute('aria-selected', on ? 'true' : 'false');
    });
  }

  /**
   * Paint the header the way a FLIP should: the outgoing label steps aside, the new one arrives.
   *
   * ⚠️ ONE FRAME BETWEEN THE TWO CLASSES, VIA rAF. Adding `is-leaving` and removing it in the same
   * task is a no-op — the browser never renders the intermediate state, so nothing transitions and
   * the text would simply cut. The rAF is what gives the leaving state a frame to exist in.
   *
   * ⚠️ THE DOTS ARE NOT PART OF THE SWAP. They carry their own `background`/`transform` transition
   * already (`.wd-present__dot`), so re-marking the active one animates on its own; wrapping them
   * in the label's fade would make the moving dot disappear and reappear rather than travel.
   *
   * Reduced motion falls through to a plain repaint: the guard is the media query, not a constant,
   * so it answers the same question the rest of the file does.
   */
  function swapHeader() {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (reduce || !modelEl || modelEl.hidden) { paintHeader(); return; }
    modelEl.classList.add('is-leaving');
    requestAnimationFrame(() => {
      window.setTimeout(() => {
        paintHeader();
        requestAnimationFrame(() => modelEl.classList.remove('is-leaving'));
      }, HEADER_SWAP_MS);
    });
  }

  /** Rebuilt rather than appended-to, so it cannot drift from `slides`. */
  function buildDots() {
    dotsEl.replaceChildren(...slides.map((s, i) => h('button', {
      type: 'button', class: 'wd-present__dot', role: 'tab',
      'aria-label': productLabel(s.product) || `Model ${i + 1}`,
      onClick: () => void go(i),
    })));
  }

  /**
   * Move to slide `i` (wrapping). A no-op for the slide already on the stage.
   *
   * ⛔ ONE FLIP AT A TIME. `applyEnvelope` is a wholesale restore plus a 3D model rebuild — hundreds
   * of milliseconds — and the arrows are two taps apart on a phone. Two overlapping restores would
   * interleave a bumper's profile with a dumbbell's geometry, and the loser would win.
   */
  async function go(i) {
    if (!active || flipping || slides.length < 2) return;
    const next = ((i % slides.length) + slides.length) % slides.length;
    if (next === loaded) return;

    flipping = true;
    prevBtn.disabled = true;
    nextBtn.disabled = true;
    const token = enterToken;
    try {
      const slide = slides[next];
      /* TWO KINDS OF SLIDE, ONE OF WHICH CARRIES NO DESIGN.
         · With an envelope — a model the sender opened and designed. Restore it wholesale.
         · Without — a model they PICKED but never opened. There is nothing to restore, so the model
           goes on the stage wearing the design that is already loaded, which is exactly what the
           tool does when a buyer changes product. Not a degraded case: it is the "one identity
           across the family" premise the whole product rests on. */
      const ok = slide.design
        ? await ctx.applyEnvelope?.(slide.design)
        : await ctx.switchProduct?.(slide.product).then(() => true).catch(() => false);
      if (token !== enterToken || !active) return;   // they left mid-flip
      if (!ok) return;                               // a bad envelope leaves the stage as it was
      loaded = next;
      /* ⚠ `applyEnvelope` clears `isSharedView` — correctly, since for every OTHER caller a
         wholesale swap means the design on screen is no longer the one that was shared. Here the
         swap IS the shared thing, one model further along, so the flag is put back. */
      if (ctx.state?.ui) ctx.state.ui.isSharedView = true;

      /* ⛔ THE CHROME WAITS FOR THE PLATE TO STOP MOVING (Ryan 2026-08-26).

         `applyEnvelope` / `switchProduct` resolve about a SECOND before the model has settled —
         `playHandoffEnter` is fired un-awaited by design and runs on after them (see `ctx.settled`
         in main.js). So this line used to repaint the label and slide the dot while the plate was
         still spinning, and a caption changing under a moving model reads as the model glitching.
         Ryan: *"the new model spins in, then once that's concluded the dots smoothly transition
         from one to the next and the sub-text/product name/title smoothly slides in to replace the
         old one."* That is exactly the order below.

         ⚠️ THE OLD LABEL STAYS PUT FOR THE WHOLE SPIN, deliberately. Blanking it on the way out
         would leave the header empty for a second — a gap where the answer to "what am I looking
         at" used to be, which is worse than a label that is briefly one model behind.

         ⚠️ `settled()` is the SAME promise the deferred panel refresh and the thumbnail drain
         already wait on, not a new timer chasing the animation's duration. A second schedule
         tracking a number it does not own is how these come apart.
         ⚠️ And it is re-checked after the await: a second arrow tap during the settle starts its
         own flip, and this one must not paint a header for the slide that is no longer current. */
      /* ⛔ RACED AGAINST A NET, NEVER AWAITED BARE. `settled()` hands back whatever
         `playHandoffEnter` returned, and this file does not own that promise — a path that resolves
         it late, or not at all, would leave the header showing the PREVIOUS model's name for the
         rest of the session, which is a worse fault than the one this ordering fixes. The same
         shape as `UI_ROLL_CHROME_FALLBACK_MS` in main.js and for the same reason: slack enough that
         a normal settle always wins it, so it only ever rescues a broken one.
         ⚠️ Deliberately NOT tuned to the spin's real duration — that number lives in reveal-3d.js
         and a net that tracked it would be a second copy of something it does not own. */
      await Promise.race([
        Promise.resolve(ctx.settled?.()).catch(() => {}),
        new Promise((r) => window.setTimeout(r, SETTLE_NET_MS)),
      ]);
      if (token !== enterToken || !active || loaded !== next) return;

      swapHeader();
      ctx.announce?.(`Showing ${slideLabel(slides[loaded]) || 'the next model'}.`);
    } catch {
      /* Leave the stage on whatever it was showing. A flip that fails is a flip that did not
         happen, which is a far better outcome than a half-restored plate. */
    } finally {
      flipping = false;
      prevBtn.disabled = false;
      nextBtn.disabled = false;
    }
  }

  // ── enter / leave ──────────────────────────────────────────────────────────

  /**
   * Present the design that is already on the stage.
   * @returns {boolean} whether it actually opened — the caller says something else when it did not.
   */
  function enter() {
    if (active) return true;
    /* ⛔ NOTHING TO PRESENT, SO DO NOT PRESENT. With WebGL unsupported (or the chunk still cold) the
       stage is the flat SVG fallback: there is no model to reveal, and dressing the page down to a
       full-screen presentation around a static picture would remove the editor's chrome to show
       nothing. The caller falls back to the status-bar line, which is what shipped before this. */
    if (!ctx.stage || !ctx.stage.ready()) return false;
    /* ⛔ NO TOASTS OVER A PRESENTED DESIGN (Ryan 2026-08-26). A flip runs the app's own wholesale
       restore, and that path ends in `afterHistoryNav`, which flashes "Design opened." — so
       arrowing to the second model popped a pill over a design a customer had been SENT. Muted at
       the status surface rather than at that one call site, so every other toast the app can raise
       is covered too, including ones written after this. The screen-reader announcement is
       deliberately NOT muted; see `setMuted` in ui/status-bar.js. */
    try { ctx.status?.setMuted?.(true); } catch { /* an older status bar simply keeps its pills */ }

    active = true;
    const token = ++enterToken;

    // A slide needs a PRODUCT; the design is optional (see `go`).
    slides = Array.isArray(ctx.state?.ui?.sharedSet) ? ctx.state.ui.sharedSet.filter((s) => s && s.product) : [];
    /* ⭐ ALWAYS THE LEFT-MOST DOT (Ryan 2026-08-16). The set arrives in catalogue order and the boot
       path restores `set[0]` for exactly this reason, so slide 0 IS what is on the stage — this is
       reading the same decision, not making a second one.
         It used to hunt for the live product instead, which was the right answer to the wrong
       question: the design a link opened on was whichever model the sender left on screen, so the
       lit dot moved from link to link and "first" meant nothing. Both halves had to change together
       — starting at 0 while the boot still restored the sender's stage would have lit the first dot
       over a plate showing the third. */
    loaded = 0;
    buildDots();
    paintHeader();

    overlay.classList.remove('is-exiting'); // a fast re-entry can land on a leftover exit anim
    overlay.hidden = false;
    showSurround();

    // The host clears the stage chrome and tells us how long the plate needs to finish re-framing.
    const settleMs = Number(onActiveChange?.(true)) || 0;
    try { overlay.focus({ preventScroll: true }); } catch { /* older browsers */ }

    ctx.announce?.(slides.length > 1
      ? `A design with ${slides.length} models was shared with you. Use the arrows to move between them, or choose Edit this design.`
      : 'A design was shared with you. Choose Edit this design to open it in the designer.');

    const dramatic = !ctx.stage.prefersReducedMotion;
    /* ⭐ THE SET-PIECE WAITS FOR THE STAGE — the same rule the parade learned the hard way. On a
       phone, entering presentation mode slides the plate down and grows it over `settleMs`; a reveal
       OWNS the camera, so a spin started into that swallows the growth and applies it in one step at
       the end. One motion, then the set-piece. */
    if (dramatic && settleMs) window.setTimeout(() => { void playReveal(token, true); }, settleMs);
    else void playReveal(token, dramatic);
    return true;
  }

  /**
   * Draw the live design onto the plate, optionally with the dramatic spin.
   * ⚠ `token` is re-checked after the await: the raster is real work, and the visitor may well have
   * pressed Edit while it was in flight. Without this the spin lands on an editor they are already
   * looking at.
   */
  async function playReveal(token, dramatic) {
    try {
      const profile = ctx.state?.designProfile;
      if (!profile) return;
      const raster = await ctx.stage.buildRaster(profile, profile.weightValue);
      if (!raster || token !== enterToken || !active) return;
      /* ⚠ CLEARED BEFORE `show`, NOT AFTER. Until this moment the host is deliberately rendering a
         stock plate so the weight rolls in plain; `show` is the spin that dresses it, and it reads
         the dye through `plateBodyColor`, which is one of the things being held. Clearing after
         would spin the design on over a plate still held black. */
      ctx.sharedRevealDone?.();
      ctx.stage.show(raster, { dramatic, bodyColor: profile.bodyColor });
    } catch {
      /* A failed raster leaves whatever the stage already had on it — which is this same design,
         drawn by the ordinary boot path. The presentation is still true, it just did not get its
         entrance; that is not worth a message to a customer. */
    }
  }

  /** Leave presentation for the real designer. The one door, and it is never conditional. */
  function leave() {
    if (!active) return;
    active = false;
    hideSurround();
    enterToken += 1; // orphan any set-piece or flip still in flight
    // The editor gets its pills back the moment it is the editor again.
    try { ctx.status?.setMuted?.(false); } catch { /* nothing to restore */ }
    // A label mid-swap must not be left faded out in the editor's own header.
    try { modelEl.classList.remove('is-leaving'); } catch { /* not mounted */ }
    /* ⛔ AND THIS BOOT STOPS BEING "A SHARED VIEW" HERE. The host reads `isSharedView` to decide
       where the plate rests and whether the pane may open — true while a presentation is on screen
       or mid-flip, and false the moment someone asks to edit. Left set, the editor they just opened
       would go on being framed as though the pane were hidden, and the next product switch would
       centre a plate that now has a 320px panel beside it. */
    if (ctx.state?.ui) ctx.state.ui.isSharedView = false;
    /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — ⛔ WHICH RECORD A SAVE WOULD WRITE TO,
       decided by the slide they are leaving on. Their own rung carries `origin`, the share id of
       the submission the rep's Deal Dashboard reads — NOT this combined link, which is a different
       record and one the rep never opens from the deal. Cleared on any other slide, which is what
       makes "a customer may save only their own design" true rather than merely displayed. */
    if (ctx.state?.ui) {
      const _s = slides[loaded];
      if (_s && _s.authored === 'customer' && _s.origin) ctx.state.ui.sharedOrigin = _s.origin;
      else if (_s && _s.authored === 'rep') ctx.state.ui.sharedOrigin = '';
    }
    onActiveChange?.(false);
    ctx.announce?.('Editing the shared design.');

    /* Bring the full-resolution editor texture back. The presentation drew at the stage's lighter
       raster size (it is not zoom-inspected); the editor is, so the swap belongs on the way out.
       ⚠ Deferred behind the chrome's return for the same reason the parade defers it: `restore()`
       is hundreds of milliseconds of synchronous main-thread work, and landing it on top of the
       sheet's rise is what a "snap" looks like. */
    const finishRestore = () => { if (!active) ctx.stage?.restore?.(); };

    if (ctx.stage?.prefersReducedMotion) {
      overlay.hidden = true;
      finishRestore();
      onEdit?.();
      return;
    }

    overlay.classList.add('is-exiting');
    if (typeof ctx.afterSheetSettles === 'function') ctx.afterSheetSettles(finishRestore, 900);
    else if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => requestAnimationFrame(finishRestore));
    else finishRestore();

    /* Hidden when the slide-out ENDS, not on a clock — the parade's note applies verbatim: a timer
       started when the tap was handled, while the animation starts when the style change is
       processed, so a fixed hide cancels the controls mid-flight. `animationend` bubbles, so one
       listener covers header, arrows and bar; the timeout is only the floor for a browser that
       never runs them. */
    let hideTimer = 0;
    const finishHide = () => {
      overlay.removeEventListener('animationend', onOutEnd);
      window.clearTimeout(hideTimer);
      if (!active) { overlay.hidden = true; overlay.classList.remove('is-exiting'); }
    };
    const onOutEnd = () => finishHide();
    overlay.addEventListener('animationend', onOutEnd);
    hideTimer = window.setTimeout(finishHide, 600);

    onEdit?.();
  }

  return {
    overlayEl: overlay,
    /** The logo and the contact line; the host mounts them on the root, outside the canvas. */
    surroundEls: [logoEl, contactEl],
    enter,
    exit: leave,
    isActive: () => active,
    /**
     * Would `enter()` actually open? Asked by the host BEFORE it takes the browser window, because
     * locking the viewport around a presentation that then refuses to start is the dead end this
     * whole module is written to avoid — the same guard, asked one step earlier.
     */
    canPresent: () => !!(ctx.stage && ctx.stage.ready()),
    /** For probes: how many models this link carried. */
    slideCount: () => slides.length,
  };
}
